import {
  BadgeProps,
  CheckIcon,
  DraftIcon,
  ErrorIcon,
  FolderIcon,
  MeetingIcon,
  PauseIcon,
  PlayIcon,
  RefreshIcon,
} from "@hightouch/htui";

import {
  DerivedCampaignStatus,
  GetCampaignsQuery,
  GetCampaignAllStatsQuery,
} from "src/graphql";

import type { RenderedOutput } from "src/cep/messages/types";
import { ChannelType } from "src/types/cep";
import { HourglassIcon } from "src/ui/icons";

export const DerivedStatusToBadgeProps: Record<
  DerivedCampaignStatus,
  Pick<BadgeProps, "variant" | "isPending" | "svgIcon" | "progress">
> = {
  [DerivedCampaignStatus.Draft]: { variant: "ghost", isPending: true },
  [DerivedCampaignStatus.Scheduled]: {
    variant: "info",
    isPending: true,
    svgIcon: MeetingIcon,
  },
  [DerivedCampaignStatus.Activated]: { variant: "info", isPending: true },
  // TODO: add real progress once that's available
  [DerivedCampaignStatus.Running]: {
    variant: "info",
    progress: "indeterminate",
  },
  [DerivedCampaignStatus.Paused]: { variant: "warning" },
  [DerivedCampaignStatus.Completed]: { variant: "success" },
  [DerivedCampaignStatus.Archived]: { variant: "subtle" },
  [DerivedCampaignStatus.Error]: { variant: "error", svgIcon: ErrorIcon },
};

export const DerivedStatusToIconMap: Record<
  DerivedCampaignStatus,
  typeof DraftIcon
> = {
  [DerivedCampaignStatus.Draft]: DraftIcon,
  [DerivedCampaignStatus.Scheduled]: MeetingIcon,
  [DerivedCampaignStatus.Activated]: RefreshIcon,
  [DerivedCampaignStatus.Running]: PlayIcon,
  [DerivedCampaignStatus.Paused]: PauseIcon,
  [DerivedCampaignStatus.Completed]: CheckIcon,
  [DerivedCampaignStatus.Archived]: FolderIcon,
  [DerivedCampaignStatus.Error]: ErrorIcon,
};

export const DerivedStatusToLabelMap: Record<DerivedCampaignStatus, string> = {
  [DerivedCampaignStatus.Draft]: "Draft",
  [DerivedCampaignStatus.Scheduled]: "Scheduled",
  [DerivedCampaignStatus.Activated]: "Active",
  [DerivedCampaignStatus.Running]: "In progress",
  [DerivedCampaignStatus.Paused]: "Paused",
  [DerivedCampaignStatus.Completed]: "Completed",
  [DerivedCampaignStatus.Archived]: "Archived",
  [DerivedCampaignStatus.Error]: "Error",
};

/**
 * A campaign's staged draft, tracked separately from the derived status because
 * a draft can sit against a campaign in any non-terminal status.
 */
export enum CampaignDraftState {
  InProgress = "in_progress",
  PendingApproval = "pending_approval",
}

export const DraftStateToBadgeProps: Record<
  CampaignDraftState,
  Required<Pick<BadgeProps, "variant" | "isPending" | "svgIcon">>
> = {
  [CampaignDraftState.InProgress]: {
    variant: "ghost",
    isPending: true,
    svgIcon: DraftIcon,
  },
  [CampaignDraftState.PendingApproval]: {
    variant: "accent",
    isPending: true,
    svgIcon: HourglassIcon,
  },
};

export const DraftStateToLabelMap: Record<CampaignDraftState, string> = {
  [CampaignDraftState.InProgress]: "In progress",
  [CampaignDraftState.PendingApproval]: "Pending approval",
};

export type CampaignDetailState = {
  autoFocusName?: boolean;
};

export type Campaign = GetCampaignsQuery["campaigns"][number];
export type CampaignAllTimeStats =
  GetCampaignAllStatsQuery["getCampaignAllStats"]["allTime"][number];
export type CampaignHourlyEntry =
  GetCampaignAllStatsQuery["getCampaignAllStats"]["hourly"][number];

export type CampaignWithMetrics = Campaign & {
  channels: ChannelType[];
  derivedStatus?: DerivedCampaignStatus;
  subtitle?: string;
  /** Not `subtitle`, which falls back to a push title. */
  subject?: string;
  preheader?: string;
  renderedOutput?: RenderedOutput;
};
